import axios from 'axios';
import { getToken } from './authService';

// Backend API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Interface for transaction data
export interface Transaction {
  id: string;
  name: string;
  amount: number;
  date: string;
  category: string[];
  pending: boolean;
}

// Interface for account data
export interface Account {
  account_id: string;
  balances: {
    available: number;
    current: number;
    limit: number | null;
  };
  name: string;
  type: string;
  subtype: string | null;
}

class PlaidService {
  private linkToken: string | null = null;

  async createLinkToken(): Promise<string> {
    try {
      console.log('Creating Plaid link token...');
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/plaid/create_link_token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const linkToken = response.data.link_token;
      if (!linkToken) {
        throw new Error('No link token received from server');
      }
      this.linkToken = linkToken;
      console.log('Plaid link token created successfully');
      return linkToken;
    } catch (error) {
      console.error('Error creating Plaid link token:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to create Plaid link token. Please try again.');
      }
      throw error;
    }
  }

  async exchangePublicToken(publicToken: string): Promise<void> {
    try {
      console.log('Exchanging public token for access token...');
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/plaid/exchange-token`,
        { public_token: publicToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Public token exchanged successfully');
      return response.data;
    } catch (error) {
      console.error('Error exchanging public token:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to exchange public token. Please try again.');
      }
      throw error;
    }
  }

  async fetchTransactions(retryCount = 0): Promise<Transaction[]> {
    try {
      console.log('Fetching transactions...');
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/plaid/fetch-transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Transactions fetched successfully:', {
        count: response.data.transactions?.length || 0,
        total: response.data.total_transactions,
        request_id: response.data.request_id
      });
      return response.data.transactions || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Failed to fetch transactions';
        console.error('API Error:', error.response?.data);
        
        // Check if this is the initial sync delay error
        if (errorMessage.includes('not yet ready') && retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
          console.log(`Initial sync not ready. Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchTransactions(retryCount + 1);
        }
        
        throw new Error(errorMessage);
      }
      throw error;
    }
  }

  async getPersistedTransactions(): Promise<Transaction[]> {
    try {
      console.log('Fetching persisted transactions...');
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/plaid/transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Persisted transactions fetched successfully');
      return response.data.transactions || [];
    } catch (error) {
      console.error('Error fetching persisted transactions:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to fetch persisted transactions. Please try again.');
      }
      throw error;
    }
  }

  getLinkToken(): string | null {
    return this.linkToken;
  }
}

const plaidServiceInstance = new PlaidService();

export default plaidServiceInstance;

// Function to get accounts
export const getAccounts = async (accessToken: string): Promise<Account[]> => {
  try {
    console.log('Fetching accounts...');
    const response = await axios.post('/api/accounts', {
      access_token: accessToken
    });
    
    console.log('Accounts fetched successfully');
    return response.data.accounts;
  } catch (error) {
    console.error('Error fetching accounts:', error);
    if (axios.isAxiosError(error)) {
      console.error('API Error:', error.response?.data);
    }
    throw new Error('Failed to fetch accounts. Please try again.');
  }
}; 